import { proxyCustomElement, HTMLElement, h, Host } from '@stencil/core/internal/client';
import { W as WidthGetterAlgorithm } from './roche-image.types.js';
import { t as throttle, d as debounce } from './throttle.js';

const rocheScaleOnScrollCss = ":host{display:block}:host ::slotted(roche-image){transition:all 400ms;transform-origin:top center;transform:scale(clamp(1, var(--scale-factor), 2));transition-timing-function:cubic-bezier(0, 0, 0, 1)}";
const RocheScaleOnScrollStyle0 = rocheScaleOnScrollCss;

const RocheScaleOnScroll = /*@__PURE__*/ proxyCustomElement(class RocheScaleOnScroll extends HTMLElement {
    /* returns true if the component is inside the viewport or 30% of the WindowHeight above */
    static isInView(windowHeight, spaceToViewTop) {
        const visibilityOffset = -0.3;
        return !(spaceToViewTop > windowHeight || spaceToViewTop < windowHeight * visibilityOffset);
    }
    /* returns the maximum Width if the parameter is lager. */
    getMaxWidth(width) {
        return Math.min(width, this.maxAnimationWidth);
    }
    getAnimationPercentage(spaceToViewTop, animationStartPx, imageAnimationDistance) {
        const animationPercentage = (this.maxAnimationDistance - spaceToViewTop - animationStartPx)
            / imageAnimationDistance;
        return Math.min(Math.max(animationPercentage, 0), 1);
    }
    setHeight() {
        this.initialSpaceToViewTop = this.element.getBoundingClientRect().top + window.scrollY;
        this.maxAnimationDistance = Math.min(this.initialSpaceToViewTop, window.innerHeight);
        if (window.innerHeight > this.initialSpaceToViewTop) {
            this.animationEndPercentage = 1;
            this.animationStartPercentage = 0.1;
        }
        this.element.style.setProperty('height', `${this.getImageHeight() * (1 + this.getWidthPercentage())}px`);
    }
    /*
     * In order to improve the performance, this method employs a heuristic check
     * for when the computation can be skipped.  When resizing the window, this
     * heuristic is not sufficient and should not be taken into account.  Setting
     * the `forceRecalculation` parameter to `true` disables the check and forces
     * a recalculation.
     */
    setScaleFactor(forceRecalculation = false) {
        const windowHeight = window.innerHeight;
        const spaceToViewTop = this.element.getBoundingClientRect().top;
        if (!forceRecalculation && !RocheScaleOnScroll.isInView(windowHeight, spaceToViewTop)) {
            return;
        }
        const imageAnimationDistance = this.maxAnimationDistance
            - this.getAnimationStartPx() - this.getAnimationEndPx();
        const animationPercentage = this.getAnimationPercentage(spaceToViewTop, this.getAnimationStartPx(), imageAnimationDistance);
        const scaleFactor = 1 + (this.getWidthPercentage() * animationPercentage);
        this.element.style.setProperty('--scale-factor', `${scaleFactor}`);
    }
    handleScroll() {
        this.setScaleFactor();
    }
    handleResize() {
        this.setHeight();
        this.setScaleFactor(true);
    }
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.animationStartPercentage = 0.25;
        this.animationEndPercentage = 0.75;
        this.maxAnimationWidth = 1864;
        this.initialSpaceToViewTop = 0;
        this.maxAnimationDistance = 0;
        this.handleScroll = throttle(this.handleScroll.bind(this), 35);
        this.handleResize = debounce(this.handleResize.bind(this), 100);
    }
    componentDidLoad() {
        var _a, _b;
        this.imageElement = this.getFirstImage();
        if (!this.imageElement) {
            return;
        }
        this.imageElement.widthGetterAlgorithm = WidthGetterAlgorithm.WITHOUT_TRANSFORM;
        (_b = (_a = this.imageElement).reinitialize) === null || _b === void 0 ? void 0 : _b.call(_a);
        this.handleResize();
    }
    getFirstImage() {
        var _a;
        const slot = this.element.shadowRoot.querySelector('slot');
        return (_a = slot.assignedElements({ flatten: true })
            .filter((element) => element.tagName === 'ROCHE-IMAGE')) === null || _a === void 0 ? void 0 : _a[0];
    }
    getImageHeight() {
        var _a, _b, _c;
        if (typeof ((_a = this.imageElement) === null || _a === void 0 ? void 0 : _a.forceHeight) === 'number') {
            return this.imageElement.forceHeight;
        }
        const imageWidth = this.imageElement.offsetWidth;
        const [width, height] = (_c = (_b = this.imageElement) === null || _b === void 0 ? void 0 : _b.ratio) === null || _c === void 0 ? void 0 : _c.split(':').map(parseFloat);
        return width && height ? Math.floor((imageWidth * height) / width) : 0;
    }
    getAnimationStartPx() {
        return this.maxAnimationDistance * this.animationStartPercentage;
    }
    getAnimationEndPx() {
        return this.maxAnimationDistance * (1 - this.animationEndPercentage);
    }
    getWidthPercentage() {
        const elementWidth = this.element.getBoundingClientRect().width;
        return (this.getMaxWidth(window.innerWidth) - elementWidth) / elementWidth;
    }
    // eslint-disable-next-line class-methods-use-this
    render() {
        return (h(Host, { key: '0df1a5226c7bd01352eb061a219c4ebce811e92a' }, h("slot", { key: '684b68f3a46f4f59aadf83a7ab55dc3abc83d5ea' })));
    }
    get element() { return this; }
    static get style() { return RocheScaleOnScrollStyle0; }
}, [1, "roche-scale-on-scroll", undefined, [[9, "scroll", "handleScroll"], [9, "resize", "handleResize"]]]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["roche-scale-on-scroll"];
    components.forEach(tagName => { switch (tagName) {
        case "roche-scale-on-scroll":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, RocheScaleOnScroll);
            }
            break;
    } });
}

export { RocheScaleOnScroll as R, defineCustomElement as d };

//# sourceMappingURL=roche-scale-on-scroll2.js.map